import React, { useCallback, useEffect, useRef, useState } from "react";

import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';

import './Menu.scss';

export interface IMenuProps {
    navigateTo: (elementName: string) => void;
}

const NAV_ITEMS: { id: string; label: string; emergency?: boolean }[] = [
    { id: "home", label: "Home" },
    { id: "philosophy", label: "Philosophy" },
    { id: "providers", label: "Provider" },
    { id: "services", label: "Services" },
    { id: "faq", label: "FAQ" },
    { id: "emergency", label: "Emergency", emergency: true },
    { id: "contact", label: "Contact" },
];

const SECTION_IDS = NAV_ITEMS.map((item) => item.id);
const SCROLL_OFFSET = 96;

export default function Menu(props: IMenuProps) {
    const [expanded, setExpanded] = useState(false);
    const [activeSection, setActiveSection] = useState<string>("home");
    const navigationLockRef = useRef<{ id: string; until: number } | null>(null);

    const updateActiveSection = useCallback(() => {
        if (navigationLockRef.current && Date.now() < navigationLockRef.current.until) {
            setActiveSection(navigationLockRef.current.id);
            return;
        }
        navigationLockRef.current = null;

        const docHeight = document.documentElement.scrollHeight;
        const scrollBottom = window.innerHeight + window.scrollY;

        if (scrollBottom >= docHeight - 60) {
            setActiveSection("contact");
            return;
        }

        const contactElement = document.getElementById("contact");
        if (contactElement) {
            const contactTop = contactElement.getBoundingClientRect().top;
            if (contactTop <= SCROLL_OFFSET + 80) {
                setActiveSection("contact");
                return;
            }
        }

        let current = SECTION_IDS[0];
        for (const id of SECTION_IDS) {
            if (id === "contact") {
                continue;
            }
            const element = document.getElementById(id);
            if (element && element.getBoundingClientRect().top <= SCROLL_OFFSET) {
                current = id;
            }
        }

        setActiveSection(current);
    }, []);

    useEffect(() => {
        updateActiveSection();
        window.addEventListener("scroll", updateActiveSection, { passive: true });
        window.addEventListener("resize", updateActiveSection);
        window.addEventListener("scrollend", updateActiveSection);

        return () => {
            window.removeEventListener("scroll", updateActiveSection);
            window.removeEventListener("resize", updateActiveSection);
            window.removeEventListener("scrollend", updateActiveSection);
        };
    }, [updateActiveSection]);

    const handleNavigate = (sectionId: string) => {
        navigationLockRef.current = { id: sectionId, until: Date.now() + 900 };
        setActiveSection(sectionId);
        props.navigateTo(sectionId);
        setExpanded(false);
    };

    return (
        <Navbar expand="lg" className="bg-body-tertiary site-navbar" fixed="top" expanded={expanded}>
            <Container>
                <Navbar.Brand
                    className="site-navbar__brand"
                    onClick={() => handleNavigate("home")}
                >
                    <img
                        alt="Better Days Psychiatry and Family Practice"
                        src="../../BetterDaysMenu.png"
                        width="50"
                        height="50"
                        className="site-navbar__logo"
                    />
                    <span className="site-navbar__brand-text">Better Days</span>
                </Navbar.Brand>

                <Navbar.Toggle
                    aria-controls="site-navbar-nav"
                    aria-label="Toggle navigation"
                    onClick={() => setExpanded(!expanded)}
                />

                <Navbar.Collapse id="site-navbar-nav">
                    <Nav className="site-navbar__nav ms-auto">
                        {NAV_ITEMS.map((item) => (
                            <Nav.Link
                                key={item.id}
                                className={[
                                    "site-navbar__link",
                                    activeSection === item.id ? "active" : "",
                                    item.emergency ? "site-navbar__link--emergency" : "",
                                ].filter(Boolean).join(" ")}
                                onClick={() => handleNavigate(item.id)}
                                active={activeSection === item.id}
                            >
                                {item.label}
                            </Nav.Link>
                        ))}
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
}
